import { authRequest } from '../../../core/api/authApiClient';
import i18n from '../../../i18n';
import type { SettlementRepository } from '../Domain/repository/SettlementRepository';
import type { 
  SearchSettlementsRequest, 
  SearchSettlementsResponse,
  CommissionSumRequest,
  CommissionSumResponse,
  CommissionSearchRequest,
  CommissionSearchResponse,
  SettlementSearchParam,
  TransactionSearchRequest,
  TransactionSearchResponse,
} from '../Domain/entity/SettlementTypes';

/** POST /transactionsall/m/sch 보강 조회용 (도메인 TransactionSearchRequest 와 별개) */
type TransactionsAllSchRequest = {
  params: SettlementSearchParam[];
  sort?: string;
  limit?: number;
  offset?: number;
};

type TransactionsAllSchItem = {
  transaction_id?: string;
  email?: string;
  comment?: string;
  sponsor_start_month?: string | number;
  sponsor_start_day?: string | number;
  sponsor_start_year?: string | number;
};

type TransactionsAllSchResponse = {
  statusCode?: number;
  result_message?: string;
  data?: TransactionsAllSchItem[];
};

const mapCommissionParamsToTransactionParams = (params: SettlementSearchParam[]) =>
  params.map((param) =>
    param.fld === 'pay_date'
      ? { ...param, fld: 'createdAt' }
      : param
  );

const normalizeDatePart = (value?: string | number) => {
  if (value === undefined || value === null) return '';
  const stringValue = String(value).trim();
  if (!stringValue) return '';
  return stringValue.padStart(2, '0');
};

const buildSponsorStartDate = (item: TransactionsAllSchItem) => {
  const year = String(item.sponsor_start_year ?? '').trim();
  const month = normalizeDatePart(item.sponsor_start_month);
  const day = normalizeDatePart(item.sponsor_start_day);
  if (!year || !month || !day) return undefined;
  return `${year}-${month}-${day}`;
};

/**
 * Settlement Repository 구현체
 */
export class SettlementRepositoryImpl implements SettlementRepository {
  async searchSettlements(request: SearchSettlementsRequest): Promise<SearchSettlementsResponse> {
    // POST /settlement/m/sch 엔드포인트 호출
    const res = await authRequest<any>('/settlement/m/sch', {
      method: 'POST',
      body: request,
    });

    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      throw new Error(String(res.result_message || i18n.t('common.repositoryErrors.settlementFetchFailed')));
    }
    return res as SearchSettlementsResponse;
  }
  
  async getCommissionSum(request: CommissionSumRequest): Promise<CommissionSumResponse> {
    // POST /mgmtcommission/m/sum 엔드포인트 호출
    const res = await authRequest<any>('/mgmtcommission/m/sum', {
      method: 'POST',
      body: request,
    });

    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      throw new Error(String(res.result_message || i18n.t('common.repositoryErrors.commissionSumFetchFailed')));
    }
    return res as CommissionSumResponse;
  }
  
  async searchCommissions(request: CommissionSearchRequest): Promise<CommissionSearchResponse> {
    // POST /mgmtcommission/m/sch 엔드포인트 호출 (offset은 URL 쿼리 파라미터로 전달)
    const offset = request.offset ?? 0;
    const path = offset > 0 ? `/mgmtcommission/m/sch?offset=${offset}` : '/mgmtcommission/m/sch';
    const commissionRes = await authRequest<any>(path, {
      method: 'POST',
      body: request,
    });

    if (commissionRes.statusCode !== undefined && commissionRes.statusCode !== 200 && commissionRes.statusCode !== 0) {
      throw new Error(String(commissionRes.result_message || i18n.t('common.repositoryErrors.commissionFetchFailed')));
    }

    const baseResponse = commissionRes as CommissionSearchResponse;
    if (!baseResponse.data?.length) {
      return baseResponse;
    }

    // 수수료 조회와 함께 transactions 조회를 추가해 email/comment/시작일 정보를 보강한다.
    try {
      const transactionLimit = Math.max(request.limit ?? 0, baseResponse.data.length, 100);
      const transactionRequest: TransactionsAllSchRequest = {
        params: mapCommissionParamsToTransactionParams(request.params),
        sort: (request.sort ?? 'createdAt:desc').replace(/created_at/g, 'createdAt'),
        limit: transactionLimit,
        offset: request.offset ?? 0,
      };

      const transactionPath = offset > 0 ? `/transactionsall/m/sch?offset=${offset}` : '/transactionsall/m/sch';
      const transactionRes = await authRequest<TransactionsAllSchResponse>(transactionPath, {
        method: 'POST',
        body: transactionRequest,
      });

      if (
        transactionRes.statusCode !== undefined &&
        transactionRes.statusCode !== 200 &&
        transactionRes.statusCode !== 0
      ) {
        throw new Error(String(transactionRes.result_message || i18n.t('common.repositoryErrors.transactionSearchFailed')));
      }

      const transactionMap = new Map<string, TransactionsAllSchItem>();
      (transactionRes.data ?? []).forEach((item) => {
        if (!item.transaction_id) return;
        transactionMap.set(String(item.transaction_id), item);
      });

      const commonData = baseResponse.data.map((item) => {
        const transaction = transactionMap.get(item.transaction_id);
        if (!transaction) {
          return item;
        }

        return {
          ...item,
          email: transaction.email ?? item.email ?? '',
          comment: transaction.comment ?? item.comment ?? '',
          sponsor_start_month: transaction.sponsor_start_month ?? item.sponsor_start_month,
          sponsor_start_day: transaction.sponsor_start_day ?? item.sponsor_start_day,
          sponsor_start_year: transaction.sponsor_start_year ?? item.sponsor_start_year,
          sponsor_start_date: buildSponsorStartDate(transaction) ?? item.sponsor_start_date,
        };
      });

      return {
        ...baseResponse,
        data: commonData,
      };
    } catch {
      // 보강 API 실패 시에도 기본 결제 내역은 유지해 목록 누락을 방지한다.
      return baseResponse;
    }
  }

  async searchTransactions(request: TransactionSearchRequest): Promise<TransactionSearchResponse> {
    const offset = request.offset ?? 0;
    const path = offset > 0 ? `/transactions/m/sch?offset=${offset}` : '/transactions/m/sch';
    const body: Record<string, unknown> = {
      params: request.params,
      sort: request.sort ?? 'createdAt:desc',
      limit: request.limit ?? 100,
      offset,
    };
    if (request.as !== undefined) body.as = request.as;
    if (request.useLinking !== undefined) body.useLinking = request.useLinking;

    const res = await authRequest<any>(path, {
      method: 'POST',
      body,
    });

    if (res.statusCode !== undefined && res.statusCode !== 200 && res.statusCode !== 0) {
      throw new Error(String(res.result_message || i18n.t('common.repositoryErrors.donorTransactionFetchFailed')));
    }
    return res as TransactionSearchResponse;
  }
}

